import { IRouteStop } from 'features/Routes/types'
import { isEmpty } from 'lodash'

const GOOGLE_POINTS_LIMIT = 22

/**
 * Google Maps API has a limit of 23 waypoints (stops) per route.
 * This function splits the stops into chunks of 22 stops each.
 * @param stops
 * @returns
 * @example
 *
 *  with a limit of 2 stops per chunk
 * const stops = [
 *  { id: '1', name: 'Stop 1', position: { lat: 1, lng: 1 } },
 * { id: '2', name: 'Stop 2', position: { lat: 2, lng: 2 } },
 * { id: '3', name: 'Stop 3', position: { lat: 3, lng: 3 } },
 * { id: '4', name: 'Stop 4', position: { lat: 4, lng: 4 } }
 * ]
 *
 * const stopsChunks = getStopsChunks(stops)
 *
 * // stopsChunks = [
 * //   [
 * //     { id: '1', name: 'Stop 1', position: { lat: 1, lng: 1 } },
 * //     { id: '2', name: 'Stop 2', position: { lat: 2, lng: 2 } }
 * //   ],
 * //   [
 * //     { id: '2', name: 'Stop 2', position: { lat: 2, lng: 2 } },
 * //     { id: '3', name: 'Stop 3', position: { lat: 3, lng: 3 } }
 * //   ],
 * //   [
 * //     { id: '3', name: 'Stop 3', position: { lat: 3, lng: 3 } },
 * //     { id: '4', name: 'Stop 4', position: { lat: 4, lng: 4 } }
 * //   ]
 * // ]
 *
 */
const getStopsChunks = (stops: IRouteStop[], stopsLimitByChunks: number): IRouteStop[][] => {
  let currentChunkIndex = 0
  return stops.reduce((result: IRouteStop[][], stop, stopIndex): IRouteStop[][] => {
    const shouldCreateNewChunk =
      stopIndex > 0 && stopIndex % (stopsLimitByChunks - 1) === 0 && stopIndex !== stops.length - 1
    if (shouldCreateNewChunk) {
      result[currentChunkIndex].push(stop)
      currentChunkIndex++
    }
    if (!result[currentChunkIndex]) {
      result[currentChunkIndex] = []
    }
    result[currentChunkIndex].push(stop)
    return result
  }, [])
}

const getGoogleDirectionsRouteByStopsChunk = async (stopsChunk: IRouteStop[]): Promise<google.maps.DirectionsRoute> => {
  const firstStop = stopsChunk[0]
  const lastStop = stopsChunk[stopsChunk.length - 1]
  const intermediateStops = stopsChunk.slice(1, stopsChunk.length - 1)
  const request = {
    origin: firstStop.position,
    destination: lastStop.position,
    waypoints: intermediateStops.map((s) => ({
      location: new google.maps.LatLng(s.position)
    })),
    travelMode: google.maps.TravelMode.DRIVING
  }
  const result = await new google.maps.DirectionsService().route(request)
  return result.routes[0]
}

export const getGoogleDirectionsRoute = async (stops: IRouteStop[]): Promise<google.maps.DirectionsRoute> => {
  let directionsRoute: google.maps.DirectionsRoute | undefined
  const stopsChunks = getStopsChunks(stops, GOOGLE_POINTS_LIMIT)

  for (const stopsChunk of stopsChunks) {
    const route = await getGoogleDirectionsRouteByStopsChunk(stopsChunk)

    if (!isEmpty(directionsRoute)) {
      // Merge the legs and overview_path of the routes from the previous chunks
      directionsRoute = {
        ...(directionsRoute as google.maps.DirectionsRoute),
        legs: [...(directionsRoute as google.maps.DirectionsRoute).legs, ...route.legs],
        overview_path: [...(directionsRoute as google.maps.DirectionsRoute).overview_path, ...route.overview_path]
      }
    } else {
      // Set the first route
      directionsRoute = { ...route }
    }
  }

  return directionsRoute as google.maps.DirectionsRoute
}

export const getTotalDistanceByRoute = ({ legs }: { legs: google.maps.DirectionsLeg[] }): number => {
  const metersDistance = legs.reduce((acc: number, leg: google.maps.DirectionsLeg): number => {
    return acc + (leg.distance?.value ?? 0)
  }, 0)

  return metersDistance
}

export const getTotalDurationByRoute = (route: google.maps.DirectionsRoute): number => {
  const secondsDuration = route.legs.reduce((acc: number, leg: google.maps.DirectionsLeg): number => {
    return acc + (leg.duration?.value ?? 0)
  }, 0)

  return secondsDuration
}

export const encodeRouteShape = (route: google.maps.DirectionsRoute): string => {
  const points = route.overview_path
  const result = google.maps.geometry.encoding.encodePath(points)
  return result
}

export interface IMapPosition {
  lat: number
  lng: number
}

export const adjustMapBounds = (records: IMapPosition[], map: google.maps.Map): void => {
  const bounds = new google.maps.LatLngBounds()

  records.forEach((record) =>
    bounds.extend({
      lat: record.lat,
      lng: record.lng
    })
  )

  if (records.length > 1) {
    map.fitBounds(bounds)
  }
  map.panToBounds(bounds, 100)
}

export const LATITUDE_REGEX_STRING = '^[-+]?([1-8]?\\d(\\.\\d+)?|90(\\.0+)?)$'
export const LONGITUDE_REGEX_STRING = '^[-+]?(180(\\.0+)?|((1[0-7]\\d)|([1-9]?\\d))(\\.\\d+)?)$'
